import {useEffect, useRef, useState} from "react"
import {Draft, produce} from 'immer';

import {AsyncStatus} from "enums";
import {ISearcherOnSearch, ISearcherOptions, Searcher} from 'services';

interface IUseSearcherState<TResult> {
    canLoadMore: boolean,
    err: any,
    results: TResult[],
    status: AsyncStatus,
}

export interface IUseSearcher<TResult> extends IUseSearcherState<TResult> {
    loadMore: () => void,
    reset: () => void,
    search: (query: string) => void,
    searchDebounced: (query: string) => void,
}

// Hook to use a searcher. Expects to be passed a function that does the api call and returns results.
export function useSearcher<TResult>(onSearch: ISearcherOnSearch<TResult>, options?: ISearcherOptions): IUseSearcher<TResult> {
    // Create a searcher if we don't have one.
    const searcherRef = useRef<Searcher<TResult>>();
    if (!searcherRef.current) {
        searcherRef.current = new Searcher<TResult>(onSearch, options);
    }
    const searcher = searcherRef.current;
    // Keep the the search callback current so it's not stale.
    searcher.onSearch = onSearch;

    const [state, setState] = useState<IUseSearcherState<TResult>>({
        canLoadMore: searcher.canLoadMore,
        err: searcher.err,
        results: searcher.results,
        status: searcher.status,
    });

    // Mark ourself as "dead" when we unmount so that we don't call any methods.
    const isDeadRef = useRef(false);
    useEffect(() => {
        return () => {
            isDeadRef.current = true;
        }
    }, [])

    // We're just wiring up the searcher's properties into hooks. We're storing them in useState
    // so that we'll trigger a rerender if they change.
    function onSearcherStateChange() {
        if (isDeadRef.current) return;

        setState(produce(draft => {
            draft.canLoadMore = searcher.canLoadMore;
            draft.err = searcher.err;
            draft.results = searcher.results as Draft<TResult[]>;
            draft.status = searcher.status;
        }));
    }

    // -------------- Searcher Methods --------------
    // We're wrapping them so that we can trigger state changes when they start and finish

    function loadMore() {
        // This returns undefined if we can't load more.
        const p = searcher.tryLoadMore();
        if (p) {
            onSearcherStateChange();
            p.finally(onSearcherStateChange);
        }
    }

    function reset() {
        searcher.reset()
        onSearcherStateChange();
    }

    function search(query: string) {
        searcher.search(query)
            .finally(onSearcherStateChange);

        onSearcherStateChange();
    }

    function searchDebounced(query: string) {
        searcher.searchDebounced(query)
            .finally(onSearcherStateChange);

        onSearcherStateChange();
    }

    return {
        canLoadMore: state.canLoadMore,
        err: state.err,
        results: state.results,
        status: state.status,
        loadMore: loadMore,
        reset: reset,
        search: search,
        searchDebounced: searchDebounced,
    }
}